import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash'
import type { RootState } from 'src/redux/store'
import type { ApiStatus } from 'src/types/miscType'
import {
	fetchAllProjectsByTenantIdUtil,
	fetchAllQuotesByTenantIdUtil,
	fetchPrepareInvoicesByTenantIdUtil,
	fetchProposalsByTenantIdUtil,
	fetchPurchasesByTenantIdUtil,
} from 'src/utils/fetchUtils'
import getFormattedNumber from 'src/utils/getFormattedNumber'
import { makeErrorToast, msg } from 'src/utils/makeToast'
import { onlyUnique } from 'src/utils/miscellaneous'
import getRoundNumber from 'src/utils/roundNumber'

// Utility functions for accessing dynamic properties
const getDynamicProperty = (obj: any, key: string) => obj[key]
const setDynamicProperty = (obj: any, key: string, value: any) => {
	obj[key] = value
}

type DashboardState = {
	dashboardProjectViewData: any
	cashViewData: any
	fetchDashboardProjectViewApiStatus: ApiStatus
	error?: string | null
}
const initialState: DashboardState = {
	dashboardProjectViewData: [],
	cashViewData: [],
	fetchDashboardProjectViewApiStatus: 'idle',
	error: null,
}

// Fetches Clients that has users tenantId and the active flag is true
export const fetchDashboardProjectViewData = createAsyncThunk<
	any,
	{ startDate: string; endDate: string },
	{
		state: RootState
		rejectValue: string
	}
>(
	'dashboard/fetchDashboardProjectViewData',
	async ({ startDate, endDate }, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			const allProjects = await fetchAllProjectsByTenantIdUtil(tenantId)

			const allQuotes = await fetchAllQuotesByTenantIdUtil(tenantId)

			const allProposals = await fetchProposalsByTenantIdUtil(tenantId)
			const allPurchases = await fetchPurchasesByTenantIdUtil({
				tenantId,
			})
			const allExportPrepareInvoices =
				await fetchPrepareInvoicesByTenantIdUtil(tenantId, 'export')
			const copyAllProposals = cloneDeep(allProposals)

			const copyAllPurchases = cloneDeep(allPurchases)

			const copyAllExportPrepareInvoices = cloneDeep(
				allExportPrepareInvoices
			)
			copyAllExportPrepareInvoices?.map((p) => {
				const prepareTaxArraySum = p?.taxAmountDetails?.reduce(
					(a: any, b: any) => a + Number.parseFloat(b.taxAmount || 0),
					0
				)
				const prepareNetArraySum = p?.netAmountDetails?.reduce(
					(a: any, b: any) => a + Number.parseFloat(b.netAmount || 0),
					0
				)
				const projectIdArray = p?.netAmountDetails
					?.map((p) => p.projectId)
					?.filter((value): value is string => value != null)
					?.filter(onlyUnique)

				setDynamicProperty(p, 'projectIdList', projectIdArray)
				setDynamicProperty(p, 'taxArraySum', prepareTaxArraySum)
				setDynamicProperty(p, 'netAmountSum', prepareNetArraySum)
			})

			const projectIdArray: string[] = []
			for (const currentCopyProposal of copyAllProposals) {
				const quoteInfo = allQuotes.find(
					(quote) => quote.id === currentCopyProposal?.quoteId
				)

				if (quoteInfo) {
					setDynamicProperty(
						currentCopyProposal,
						'projectId',
						quoteInfo?.parentProjectId
					)
					if (quoteInfo?.parentProjectId) {
						projectIdArray.push(quoteInfo.parentProjectId)
					}
				} else {
					// If active flag is false
					const quoteInfoProjectId = getDynamicProperty(
						currentCopyProposal,
						'quoteInfo'
					)?.parentProjectId
					setDynamicProperty(
						currentCopyProposal,
						'projectId',
						quoteInfoProjectId
					)
					if (quoteInfoProjectId) {
						projectIdArray.push(quoteInfoProjectId)
					}
				}
			}
			for (const currentCopyAllPurchases of copyAllPurchases) {
				if (currentCopyAllPurchases?.quoteId) {
					const quoteInfo = allQuotes.find(
						(quote) => quote.id === currentCopyAllPurchases?.quoteId
					)
					if (quoteInfo && quoteInfo?.parentProjectId) {
						setDynamicProperty(
							currentCopyAllPurchases,
							'projectId',
							quoteInfo.parentProjectId
						)
						projectIdArray.push(quoteInfo.parentProjectId)
					}
				}
			}

			const uniqueProjectIdArray = [...new Set(projectIdArray)]

			const dataPerProject = []
			for (const projectId of uniqueProjectIdArray) {
				const projectInfo = allProjects.find(
					(project) => project.id === projectId
				)
				let paidAmount = 0
				let pendingAmount = 0
				let notInvoicedAmount = 0

				// ---------------------------------------For Revenues---------------------------------------
				const filterProposals = copyAllProposals.filter(
					(p) => getDynamicProperty(p, 'projectId') === projectId
				)

				//   let totalProposals = filterProposals.reduce((a, b) => a + parseFloat(b.total || 0), 0)
				//
				filterProposals
					.filter((p) => p.status !== 'Expired')
					.map((f) => {
						if (
							f.status === 'Generate' ||
							f.status === 'Generate without PO'
						) {
							notInvoicedAmount += f.total || 0
						} else {
							const invArray = f.invoices?.items
							invArray
								?.filter(
									(inv: any) =>
										inv.paymentStatus !== 'Archived'
								)
								.map((i: any) => {
									if (i.invoiceType === 'original') {
										if (i.paymentStatus === 'Paid') {
											paidAmount += i.amount
										} else {
											pendingAmount += i.amount
										}
									} else if (i.invoiceType === 'credit') {
										if (i.paymentStatus === 'Paid') {
											paidAmount -= i.amount
										} else {
											pendingAmount -= i.amount
										}
									}
								})
							if (f.status === 'Partially Generated') {
								f?.drafts?.map((draft: any) => {
									if (draft.generated) {
										return
									}
									notInvoicedAmount += draft.amount
								})
							}
						}
					})
				// ---------------------------------------For Costs---------------------------------------
				const filterAllDraftPurchases = copyAllPurchases.filter(
					(p) =>
						getDynamicProperty(p, 'projectId') === projectId &&
						p.paymentStatus === 'Draft'
				)

				const filterAllExportPrepareInvoices =
					copyAllExportPrepareInvoices.filter((p) =>
						getDynamicProperty(p, 'projectIdList')?.includes(
							projectId
						)
					)

				const filterAllExportPrepareInvoicesNotOverhead =
					filterAllExportPrepareInvoices.filter(
						(p) =>
							p.type !== 'overhead' &&
							p.type !== 'transactionsPettyCashOverhead'
					)
				console.log(
					'filterAllExportPrepareInvoicesNotOverhead: ',
					filterAllExportPrepareInvoicesNotOverhead
				)

				//   let totalCosts = filterAllExportPrepareInvoices.reduce(
				//     (a, b) => a + parseFloat(b.netAmount || 0),
				//     0
				//   )
				let paidCosts = 0
				let pendingCosts = 0
				for (const each of filterAllExportPrepareInvoicesNotOverhead) {
					const currNetAmtArray = each?.netAmountDetails
					const currNetAmtArrayForProject = currNetAmtArray
						?.filter((c: any) => c.projectId === projectId)
						?.reduce(
							(a: any, b: any) =>
								a + Number.parseFloat(b.netAmount || 0),
							0
						)
					if (each.paymentStatus === 'Paid') {
						paidCosts += currNetAmtArrayForProject
					} else if (each.paymentStatus !== 'Paid') {
						pendingCosts += currNetAmtArrayForProject
					}
				}

				const notInvoicedCosts = filterAllDraftPurchases.reduce(
					(a, b) =>
						a +
						Number.parseFloat(
							String(
								getDynamicProperty(b, 'provisionalTotal') || 0
							)
						),
					0
				)

				const totalCost =
					getRoundNumber(paidCosts) +
					getRoundNumber(pendingCosts) +
					getRoundNumber(notInvoicedCosts)
				const totalRevenue =
					getRoundNumber(paidAmount) +
					getRoundNumber(pendingAmount) +
					getRoundNumber(notInvoicedAmount)

				const newObj: any = {
					date: projectInfo?.createdOn,
					projectId: projectId,
					projectName: projectInfo?.name,
					projectNumber: projectInfo?.projectNumber,
					projectRevenueAmount: totalRevenue,
					totalDiff: totalRevenue - totalCost,
					projectCostAmount: totalCost,
					details: [
						{
							text: 'Paid',
							revenue: paidAmount,
							cost: paidCosts,
							diff: paidAmount - paidCosts,
						},
						{
							text: 'Pending',
							revenue: pendingAmount,
							cost: pendingCosts,
							diff: pendingAmount - pendingCosts,
						},
						{
							text: 'To Be Invoiced',
							revenue: notInvoicedAmount,
							cost: notInvoicedCosts,
							diff: notInvoicedAmount - notInvoicedCosts,
						},
					],
				}
				dataPerProject.push(newObj)
			}

			const filterAllOverheadExportPrepareInvoices =
				copyAllExportPrepareInvoices.filter(
					(p) =>
						p.type === 'overhead' ||
						p.type === 'transactionsPettyCashOverhead'
				)
			if (filterAllOverheadExportPrepareInvoices.length > 0) {
				let totalOverheadCost = 0
				let paidOverheadCosts = 0
				let pendingOverheadCosts = 0
				const totalOverheadRevenue = 0
				const paidOverheadAmount = 0
				const pendingOverheadAmount = 0
				const notInvoicedOverheadAmount = 0

				for (const each of filterAllOverheadExportPrepareInvoices) {
					const currNetAmtArray = each?.netAmountDetails
					const currNetAmtArrayForProject = currNetAmtArray?.reduce(
						(a: any, b: any) =>
							a + Number.parseFloat(b.netAmount || 0),
						0
					)
					totalOverheadCost += currNetAmtArrayForProject
					if (each.paymentStatus === 'Paid') {
						paidOverheadCosts += currNetAmtArrayForProject
					} else if (each.paymentStatus !== 'Paid') {
						pendingOverheadCosts += currNetAmtArrayForProject
					}
				}
				const notInvoicedOverheadCosts = 0
				const newObj: any = {
					date: dayjs().toISOString(),
					projectId: 'overhead',
					projectName: 'Overhead',
					projectNumber: '',
					projectRevenueAmount: 0,
					totalDiff: totalOverheadRevenue - totalOverheadCost,
					projectCostAmount: totalOverheadCost,
					details: [
						{
							text: 'Paid',
							revenue: paidOverheadAmount,
							cost: paidOverheadCosts,
							diff: paidOverheadAmount - paidOverheadCosts,
						},
						{
							text: 'Pending',
							revenue: pendingOverheadAmount,
							cost: pendingOverheadCosts,
							diff: pendingOverheadAmount - pendingOverheadCosts,
						},
						{
							text: 'To Be Invoiced',
							revenue: notInvoicedOverheadAmount,
							cost: notInvoicedOverheadCosts,
							diff:
								notInvoicedOverheadAmount -
								notInvoicedOverheadCosts,
						},
					],
				}
				dataPerProject.push(newObj)
			}

			// ---------------------------------------------------------All Totals For Cards-----------------------------------------------------------
			//  ! For the cards on the first page
			const totalData = []

			let paidTotalAmount = 0
			let pendingTotalAmount = 0
			let taxPaidAmount = 0
			let taxPendingAmount = 0
			let notInvoicedAmount = 0

			// ---------------------------------------For Revenues---------------------------------------
			const filterProposals = cloneDeep(copyAllProposals)

			//   let totalProposals = filterProposals.reduce((a, b) => a + parseFloat(b.total || 0), 0)
			//
			filterProposals.map((f) => {
				if (
					f.status === 'Generate' ||
					f.status === 'Generate without PO'
				) {
					notInvoicedAmount += f.total || 0
				} else {
					const invArray = f.invoices?.items
					invArray
						?.filter((inv: any) => inv.paymentStatus !== 'Archived')
						.map((i: any) => {
							if (i.invoiceType === 'original') {
								if (i.paymentStatus === 'Paid') {
									paidTotalAmount += i.amountAfterTax
									taxPaidAmount += i.taxAmount
								} else {
									pendingTotalAmount += i.amountAfterTax
									taxPendingAmount += i.taxAmount
								}
							} else if (i.invoiceType === 'credit') {
								if (i.paymentStatus === 'Paid') {
									paidTotalAmount -= i.amountAfterTax
									taxPaidAmount -= i.taxAmount
								} else {
									pendingTotalAmount -= i.amountAfterTax
									taxPendingAmount -= i.taxAmount
								}
							}
						})
					if (f.status === 'Partially Generated') {
						f?.drafts?.map((draft: any) => {
							if (draft.generated) {
								return
							}
							notInvoicedAmount += draft.amount
						})
					}
				}
			})
			// ---------------------------------------For Costs---------------------------------------
			const filterAllDraftPurchases2 = copyAllPurchases.filter(
				(p) => p.paymentStatus === 'Draft'
			)

			const filterAllExportPrepareInvoices2 = cloneDeep(
				copyAllExportPrepareInvoices
			)

			const filterAllExportPrepareInvoicesNotOverhead2 =
				filterAllExportPrepareInvoices2.filter(
					(p) =>
						p.type !== 'overhead' &&
						p.type !== 'transactionsPettyCashOverhead'
				)
			console.log(
				'filterAllExportPrepareInvoicesNotOverhead2: ',
				filterAllExportPrepareInvoicesNotOverhead2
			)

			filterAllDraftPurchases2.map((p) => {
				if (getDynamicProperty(p, 'supplierType') === 'company') {
					setDynamicProperty(
						p,
						'totalAfterTax',
						getFormattedNumber(
							0.2 *
								(getDynamicProperty(p, 'provisionalTotal') || 0)
						)
					)
				} else {
					setDynamicProperty(
						p,
						'totalAfterTax',
						getFormattedNumber(
							getDynamicProperty(p, 'provisionalTotal') || 0
						)
					)
				}
			})
			//   let totalCosts = filterAllExportPrepareInvoices.reduce(
			//     (a, b) => a + parseFloat(b.netAmount || 0),
			//     0
			//   )
			const paidTotalCosts = filterAllExportPrepareInvoicesNotOverhead2
				.filter((p) => p.paymentStatus === 'Paid')
				.reduce(
					(a, b) => a + Number.parseFloat(String(b.totalAmount || 0)),
					0
				)
			const pendingTotalCosts = filterAllExportPrepareInvoicesNotOverhead2
				.filter((p) => p.paymentStatus !== 'Paid')
				.reduce(
					(a, b) => a + Number.parseFloat(String(b.totalAmount || 0)),
					0
				)
			const taxPaidCosts = filterAllExportPrepareInvoicesNotOverhead2
				.filter((p) => p.paymentStatus === 'Paid')
				.reduce(
					(a, b) =>
						a +
						Number.parseFloat(
							String(getDynamicProperty(b, 'taxArraySum') || 0)
						),
					0
				)
			const taxPendingCosts = filterAllExportPrepareInvoicesNotOverhead2
				.filter((p) => p.paymentStatus === 'Pending')
				.reduce(
					(a, b) =>
						a +
						Number.parseFloat(
							String(getDynamicProperty(b, 'taxArraySum') || 0)
						),
					0
				)
			const notInvoicedCosts = filterAllDraftPurchases2.reduce(
				(a, b) =>
					a +
					Number.parseFloat(
						String(getDynamicProperty(b, 'provisionalTotal') || 0)
					),
				0
			)

			const filterAllOverheadExportPrepareInvoices2 =
				copyAllExportPrepareInvoices.filter(
					(p) =>
						p.type === 'overhead' ||
						p.type === 'transactionsPettyCashOverhead'
				)
			let totalOverheadCost = 0
			let paidOverheadCosts = 0
			let pendingOverheadCosts = 0
			let totalOverheadTax = 0
			let paidOverheadTax = 0
			let pendingOverheadTax = 0
			if (filterAllOverheadExportPrepareInvoices2.length > 0) {
				const totalOverheadRevenue = 0
				const paidOverheadAmount = 0
				const pendingOverheadAmount = 0
				const notInvoicedOverheadAmount = 0
				totalOverheadCost =
					filterAllOverheadExportPrepareInvoices2.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
				totalOverheadTax =
					filterAllOverheadExportPrepareInvoices2.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
				paidOverheadCosts = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus === 'Paid')
					.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
				paidOverheadTax = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus === 'Paid')
					.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
				pendingOverheadCosts = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus !== 'Paid')
					.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
				pendingOverheadTax = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus !== 'Paid')
					.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
				const notInvoicedOverheadCosts = 0
			}

			const totalCost =
				getRoundNumber(paidTotalCosts) +
				getRoundNumber(pendingTotalCosts) +
				getRoundNumber(notInvoicedCosts) +
				getRoundNumber(totalOverheadCost)

			const totalRevenue =
				getRoundNumber(paidTotalAmount) +
				getRoundNumber(pendingTotalAmount) +
				getRoundNumber(notInvoicedAmount)

			const netTaxNotInvoicedAmount = getRoundNumber(
				0.2 * notInvoicedAmount
			)
			const netTaxNotInvoicedCost = -getRoundNumber(
				0.2 * notInvoicedCosts
			)
			const netTaxNotInvoicedDiff = -(
				netTaxNotInvoicedAmount + netTaxNotInvoicedCost
			)
			//  !End
			// ----------------------------------------------------------------------------------------------------------------------------------------

			// ---------------------------------------------------------All Totals For Cards-----------------------------------------------------------
			//  ! For the cards on the first page filterd by date

			let paidTotalAmountByDate = 0
			let pendingTotalAmountByDate = 0
			let taxPaidAmountByDate = 0
			let taxPendingAmountByDate = 0
			let notInvoicedAmountByDate = 0

			// ---------------------------------------For Revenues---------------------------------------
			const allProposalsCopy = cloneDeep(copyAllProposals)
			const filterProposalsByDate = allProposalsCopy?.filter(
				(pd) =>
					dayjs(pd.createdOn).isAfter(dayjs(startDate)) &&
					dayjs(pd.createdOn).isBefore(dayjs(endDate))
			)

			if (filterProposalsByDate && filterProposalsByDate.length !== 0) {
				console.log('filterProposalsByDate: ', filterProposalsByDate)
				filterProposalsByDate.map((f) => {
					if (
						f.status === 'Generate' ||
						f.status === 'Generate without PO'
					) {
						notInvoicedAmountByDate += f.total || 0
					} else {
						const invArray = f.invoices?.items
						invArray
							?.filter(
								(inv: any) => inv.paymentStatus !== 'Archived'
							)
							.map((i: any) => {
								if (i.invoiceType === 'original') {
									if (i.paymentStatus === 'Paid') {
										console.log('i paid: ', i)
										paidTotalAmountByDate +=
											i.amountAfterTax
										taxPaidAmountByDate += i.taxAmount
									} else {
										pendingTotalAmountByDate +=
											i.amountAfterTax
										taxPendingAmountByDate += i.taxAmount
									}
								} else if (i.invoiceType === 'credit') {
									if (i.paymentStatus === 'Paid') {
										paidTotalAmountByDate -=
											i.amountAfterTax
										taxPaidAmountByDate -= i.taxAmount
									} else {
										pendingTotalAmountByDate -=
											i.amountAfterTax
										taxPendingAmountByDate -= i.taxAmount
									}
								}
							})
						if (f.status === 'Partially Generated') {
							f?.drafts?.map((draft: any) => {
								if (draft.generated) {
									return
								}
								notInvoicedAmountByDate += draft.amount
							})
						}
					}
				})
			}
			// ---------------------------------------For Costs---------------------------------------
			const filterAllDraftPurchasesByDate = copyAllPurchases.filter(
				(p) =>
					p.paymentStatus === 'Draft' &&
					dayjs(p.createdOn).isAfter(dayjs(startDate)) &&
					dayjs(p.createdOn).isBefore(dayjs(endDate))
			)

			const filterAllExportPrepareInvoicesCopy = cloneDeep(
				copyAllExportPrepareInvoices
			)

			const filterAllExportPrepareInvoicesNotOverheadByDate =
				filterAllExportPrepareInvoicesCopy.filter(
					(p) =>
						p.type !== 'overhead' &&
						p.type !== 'transactionsPettyCashOverhead' &&
						dayjs(p.createdOn).isAfter(dayjs(startDate)) &&
						dayjs(p.createdOn).isBefore(dayjs(endDate))
				)
			console.log(
				'filterAllExportPrepareInvoicesNotOverheadByDate: ',
				filterAllExportPrepareInvoicesNotOverheadByDate
			)

			filterAllExportPrepareInvoicesNotOverheadByDate.map((p) => {
				if (getDynamicProperty(p, 'supplierType') === 'company') {
					setDynamicProperty(
						p,
						'totalAfterTax',
						getFormattedNumber(
							0.2 *
								(getDynamicProperty(p, 'provisionalTotal') || 0)
						)
					)
				} else {
					setDynamicProperty(
						p,
						'totalAfterTax',
						getFormattedNumber(
							getDynamicProperty(p, 'provisionalTotal') || 0
						)
					)
				}
			})

			const paidTotalCostsByDate =
				filterAllExportPrepareInvoicesNotOverheadByDate
					.filter((p) => p.paymentStatus === 'Paid')
					.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
			const pendingTotalCostsByDate =
				filterAllExportPrepareInvoicesNotOverheadByDate
					.filter((p) => p.paymentStatus !== 'Paid')
					.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
			const taxPaidCostsByDate =
				filterAllExportPrepareInvoicesNotOverheadByDate
					.filter((p) => p.paymentStatus === 'Paid')
					.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
			const taxPendingCostsByDate =
				filterAllExportPrepareInvoicesNotOverheadByDate
					.filter((p) => p.paymentStatus === 'Pending')
					.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
			const notInvoicedCostsByDate = filterAllDraftPurchasesByDate.reduce(
				(a, b) =>
					a +
					Number.parseFloat(
						String(getDynamicProperty(b, 'totalAfterTax') || 0)
					),
				0
			)

			const filterAllOverheadExportPrepareInvoicesByDate =
				copyAllExportPrepareInvoices.filter(
					(p) =>
						(p.type === 'overhead' ||
							p.type === 'transactionsPettyCashOverhead') &&
						dayjs(p.createdOn).isAfter(dayjs(startDate)) &&
						dayjs(p.createdOn).isBefore(dayjs(endDate))
				)
			const totalOverheadCostByDate = 0
			const paidOverheadCostsByDate = 0
			const pendingOverheadCostsByDate = 0
			const totalOverheadTaxByDate = 0
			const paidOverheadTaxByDate = 0
			const pendingOverheadTaxByDate = 0
			if (filterAllOverheadExportPrepareInvoicesByDate.length > 0) {
				const totalOverheadRevenue = 0
				const paidOverheadAmount = 0
				const pendingOverheadAmount = 0
				const notInvoicedOverheadAmount = 0
				totalOverheadCost =
					filterAllOverheadExportPrepareInvoices2.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
				totalOverheadTax =
					filterAllOverheadExportPrepareInvoices2.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
				paidOverheadCosts = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus === 'Paid')
					.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
				paidOverheadTax = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus === 'Paid')
					.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
				pendingOverheadCosts = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus !== 'Paid')
					.reduce(
						(a, b) =>
							a + Number.parseFloat(String(b.totalAmount || 0)),
						0
					)
				pendingOverheadTax = filterAllOverheadExportPrepareInvoices2
					.filter((p) => p.paymentStatus !== 'Paid')
					.reduce(
						(a, b) =>
							a +
							Number.parseFloat(
								String(
									getDynamicProperty(b, 'taxArraySum') || 0
								)
							),
						0
					)
				const notInvoicedOverheadCosts = 0
			}

			const totalCostByDate =
				getRoundNumber(paidTotalCostsByDate) +
				getRoundNumber(pendingTotalCostsByDate) +
				getRoundNumber(notInvoicedCostsByDate) +
				getRoundNumber(totalOverheadCostByDate)

			const totalRevenueByDate =
				getRoundNumber(paidTotalAmountByDate) +
				getRoundNumber(pendingTotalAmountByDate) +
				getRoundNumber(notInvoicedAmountByDate)

			const netTaxNotInvoicedAmountByDate = getRoundNumber(
				0.2 * notInvoicedAmountByDate
			)
			const netTaxNotInvoicedCostByDate = -getRoundNumber(
				0.2 * notInvoicedCostsByDate
			)
			const netTaxNotInvoicedDiffByDate = -(
				netTaxNotInvoicedAmountByDate + netTaxNotInvoicedCostByDate
			)

			const newObj: any = {
				totalRevenueAmount: getRoundNumber(totalRevenue),
				totalDiff: getRoundNumber(totalRevenue - totalCost),
				totalCostAmount: getRoundNumber(totalCost),
				expected: getRoundNumber(
					pendingTotalAmount +
						notInvoicedAmount +
						-(
							pendingTotalCosts +
							pendingOverheadCosts +
							notInvoicedCosts
						) +
						-(
							taxPendingAmount +
							-(taxPendingCosts + pendingOverheadTax)
						) +
						netTaxNotInvoicedDiff
				),
				details: [
					{
						text: 'Paid',
						revenue: getRoundNumber(paidTotalAmount),
						cost: -getRoundNumber(
							paidTotalCosts + paidOverheadCosts
						),
						diff: getRoundNumber(
							paidTotalAmount -
								(paidTotalCosts + paidOverheadCosts)
						),
						netTaxRevenue: getRoundNumber(taxPaidAmount),
						netTaxCost: -getRoundNumber(
							taxPaidCosts + paidOverheadTax
						),
						netTaxTotal: -getRoundNumber(
							taxPaidAmount + -(taxPaidCosts + paidOverheadTax)
						),
						totalDiff:
							paidTotalAmount +
							// Add sum of costs and overhead, but they should always be a negative value, since its an expense
							-(paidTotalCosts + paidOverheadCosts) +
							-(
								taxPaidAmount +
								-(taxPaidCosts + paidOverheadTax)
							),
					},
					{
						text: 'Pending',
						revenue: getRoundNumber(pendingTotalAmount),
						cost: -getRoundNumber(
							pendingTotalCosts + pendingOverheadCosts
						),
						diff: getRoundNumber(
							pendingTotalAmount -
								(pendingTotalCosts + pendingOverheadCosts)
						),
						netTaxRevenue: getRoundNumber(taxPendingAmount),
						netTaxCost: -getRoundNumber(
							taxPendingCosts + pendingOverheadTax
						),
						netTaxTotal: -getRoundNumber(
							taxPendingAmount +
								-(taxPendingCosts + pendingOverheadTax)
						),
						totalDiff:
							pendingTotalAmount +
							// Add sum of costs and overhead, but they should always be a negative value, since its an expense
							-(pendingTotalCosts + pendingOverheadCosts) +
							-(
								taxPendingAmount +
								-(taxPendingCosts + pendingOverheadTax)
							),
					},
					{
						text: 'To Be Invoiced',
						revenue: getRoundNumber(notInvoicedAmount),
						cost: -getRoundNumber(notInvoicedCosts),
						diff: getRoundNumber(
							notInvoicedAmount - notInvoicedCosts
						),
						netTaxRevenue: netTaxNotInvoicedAmount,
						netTaxCost: netTaxNotInvoicedCost,
						netTaxTotal: netTaxNotInvoicedDiff,
						totalDiff:
							notInvoicedAmount +
							-notInvoicedCosts +
							netTaxNotInvoicedDiff,
					},
				],
				detailsByDate: [
					{
						text: 'Paid',
						revenue: getRoundNumber(paidTotalAmountByDate),
						cost: -getRoundNumber(
							paidTotalCostsByDate + paidOverheadCostsByDate
						),
						diff: getRoundNumber(
							paidTotalAmountByDate -
								(paidTotalCostsByDate + paidOverheadCostsByDate)
						),
						netTaxRevenue: getRoundNumber(taxPaidAmountByDate),
						netTaxCost: -getRoundNumber(
							taxPaidCostsByDate + paidOverheadTaxByDate
						),
						netTaxTotal: -getRoundNumber(
							taxPaidAmountByDate +
								-(taxPaidCostsByDate + paidOverheadTaxByDate)
						),
						totalDiff:
							paidTotalAmountByDate +
							// Add sum of costs and overhead, but they should always be a negative value, since its an expense
							-(paidTotalCostsByDate + paidOverheadCostsByDate) +
							-(
								taxPaidAmountByDate +
								-(taxPaidCostsByDate + paidOverheadTaxByDate)
							),
					},
					{
						text: 'Pending',
						revenue: getRoundNumber(pendingTotalAmountByDate),
						cost: -getRoundNumber(
							pendingTotalCostsByDate + pendingOverheadCostsByDate
						),
						diff: getRoundNumber(
							pendingTotalAmountByDate -
								(pendingTotalCostsByDate +
									pendingOverheadCostsByDate)
						),
						netTaxRevenue: getRoundNumber(taxPendingAmountByDate),
						netTaxCost: -getRoundNumber(
							taxPendingCostsByDate + pendingOverheadTaxByDate
						),
						netTaxTotal: -getRoundNumber(
							taxPendingAmountByDate +
								-(
									taxPendingCostsByDate +
									pendingOverheadTaxByDate
								)
						),
						totalDiff:
							pendingTotalAmountByDate +
							// Add sum of costs and overhead, but they should always be a negative value, since its an expense
							-(
								pendingTotalCostsByDate +
								pendingOverheadCostsByDate
							) +
							-(
								taxPendingAmountByDate +
								-(
									taxPendingCostsByDate +
									pendingOverheadTaxByDate
								)
							),
					},
					{
						text: 'To Be Invoiced',
						revenue: getRoundNumber(notInvoicedAmountByDate),
						cost: -getRoundNumber(notInvoicedCostsByDate),
						diff: getRoundNumber(
							notInvoicedAmountByDate - notInvoicedCostsByDate
						),
						netTaxRevenue: netTaxNotInvoicedAmountByDate,
						netTaxCost: netTaxNotInvoicedCostByDate,
						netTaxTotal: netTaxNotInvoicedDiffByDate,
						totalDiff:
							notInvoicedAmountByDate +
							-notInvoicedCostsByDate +
							netTaxNotInvoicedDiffByDate,
					},
				],
			}
			console.log('aaaa pendingTotalAmount: ', pendingTotalAmount)
			console.log('aaaa notInvoicedAmount: ', notInvoicedAmount)
			console.log('aaaa pendingTotalCosts: ', pendingTotalCosts)
			console.log('aaaa pendingOverheadCosts: ', pendingOverheadCosts)
			console.log('aaaa notInvoicedCosts: ', notInvoicedCosts)
			console.log(
				'aaaa tax ',
				taxPendingAmount + -(taxPendingCosts + pendingOverheadTax)
			)
			console.log('aaaa netTaxNotInvoicedDiff: ', netTaxNotInvoicedDiff)
			console.log('aaaa taxPendingAmount: ', taxPendingAmount)
			console.log('aaaa pendingOverheadTax: ', pendingOverheadTax)
			console.log('aaaa taxPendingCosts: ', taxPendingCosts)

			console.log(
				'aaaa pendingTotalCosts + pendingOverheadCosts: ',
				-(pendingTotalCosts + pendingOverheadCosts)
			)
			console.log('aaaa pendingTotalCosts: ', pendingTotalCosts)
			console.log('aaaa pendingOverheadCosts: ', pendingOverheadCosts)
			totalData.push(newObj)
			console.log('aaaa totalData: ', totalData)

			// ----------------------------------------------------------------------------------------------------------------------------------------

			return { dataPerProject, totalData }
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const dashboardSlice = createSlice({
	name: 'dashboard',
	initialState,
	reducers: {
		clearDashboardProjectViewData: (state, action) => {
			state.dashboardProjectViewData = []
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			fetchDashboardProjectViewData.pending,
			(state, action) => {
				state.fetchDashboardProjectViewApiStatus = 'loading'
			}
		)
		builder.addCase(
			fetchDashboardProjectViewData.fulfilled,
			(state, action) => {
				state.fetchDashboardProjectViewApiStatus = 'succeeded'
				state.dashboardProjectViewData = action.payload.dataPerProject
				state.cashViewData = action.payload.totalData
			}
		)
		builder.addCase(
			fetchDashboardProjectViewData.rejected,
			(state, action) => {
				state.fetchDashboardProjectViewApiStatus = 'failed'
				state.error = action.error.message
			}
		)
	},
})

export const { clearDashboardProjectViewData } = dashboardSlice.actions

export default dashboardSlice.reducer
